import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Form, InputGroup, Card, Modal, Row, Col, Spinner, Badge } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, faPlus, faTools, faEdit, faTrash, faSync, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import MainLayout from '../../layouts/MainLayout';

// Helper functions dari DaftarAset.jsx
const normalizeNumber = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.-]/g, '');
    if (cleaned === '' || cleaned === '.' || cleaned === '-') return 0;
    
    const number = parseFloat(cleaned);
    return isNaN(number) ? 0 : number;
  }
  
  return 0;
};

const formatRupiah = (value) => {
  if (value === undefined || value === null || value === '') {
    return 'Rp 0';
  }
  
  let numValue;
  
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.-]/g, '');
    numValue = cleaned ? parseFloat(cleaned) : 0;
  } else {
    numValue = Number(value);
  }
  
  if (isNaN(numValue)) {
    console.warn('Invalid number format for value:', value);
    return 'Rp 0';
  }
  
  try {
    return `Rp ${numValue.toLocaleString('id-ID')}`;
  } catch (err) {
    console.error('Error formatting number:', err);
    return `Rp ${numValue}`;
  }
};

// Modifikasi opsi tipe peralatan
const typeOptions = [
  'Perkakas Tangan',
  'Mesin',
  'Alat Potong',
  'Alat Ukur',
  'Perlengkapan Keselamatan',
  'Lainnya'
];

// Opsi kondisi untuk dropdown
const conditionOptions = [
  { value: 'baik', label: 'Baik', variant: 'success' },
  { value: 'cukup', label: 'Cukup', variant: 'info' },
  { value: 'butuh_perawatan', label: 'Butuh Perawatan', variant: 'warning' },
  { value: 'rusak', label: 'Rusak', variant: 'danger' }
];

const DaftarPeralatan = () => {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    type: '',
    brand: '',
    model: '',
    quantity: '0',
    min_stock: '0',
    condition: 'baik',
    price: '0',
    location: '',
    notes: '',
    category: 'equipment'  // Selalu tetapkan kategori equipment
  });

  // Tambahkan state untuk debugging
  const [debugMode, setDebugMode] = useState(false);

  // Tambahkan definisi fungsi attemptRecoveryFromLocalStorage DI DALAM komponen
  const attemptRecoveryFromLocalStorage = () => {
    try {
      const backupData = localStorage.getItem('rumahAkrilik_equipmentBackup');
      if (backupData) {
        try {
          const parsedBackup = JSON.parse(backupData);
          if (Array.isArray(parsedBackup) && parsedBackup.length > 0) {
            console.log("Recovering from local storage cache:", parsedBackup.length, "items");
            setEquipment(parsedBackup);
            setRecoveryMode(true);
            toast.warning("Menggunakan data cache lokal. Silakan sinkronisasi untuk data terbaru.");
            return true;
          }
        } catch (parseError) {
          console.error("Error parsing cache data:", parseError);
        }
      }
      
      // Jika tidak ada backup yang valid, coba pertahankan state saat ini jika ada
      if (Array.isArray(equipment) && equipment.length > 0) {
        console.log("Keeping current equipment state:", equipment.length, "items");
        setRecoveryMode(true);
        toast.warning("Gagal memuat data baru, menggunakan data yang sudah dimuat sebelumnya.");
        return true;
      }
      
      // Jika tidak ada data yang dapat digunakan, set ke array kosong
      console.error("No equipment data available, setting to empty array");
      setEquipment([]);
      toast.error('Gagal memuat data peralatan dan tidak ada cache lokal');
      return false;
    } catch (localErr) {
      console.error("Error accessing local backup:", localErr);
      // Hanya set equipment ke [] jika tidak ada data sebelumnya
      if (equipment.length === 0) {
        setEquipment([]);
      }
      toast.error('Gagal memuat data peralatan');
      return false;
    }
  };

  useEffect(() => {
    fetchEquipment();
  }, []);

  // Perbaiki fungsi fetchEquipment seperti di DaftarAset yang sudah berhasil
  const fetchEquipment = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      
      if (!token) {
        toast.error('Sesi login telah berakhir');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        return;
      }

      // Periksa cache lokal terlebih dahulu jika tidak forceRefresh
      if (!forceRefresh) {
        const cachedData = localStorage.getItem('rumahAkrilik_equipmentBackup');
        const cacheTimestamp = localStorage.getItem('rumahAkrilik_equipmentBackupDate');
        
        if (cachedData && cacheTimestamp) {
          // Periksa apakah cache masih segar (kurang dari 5 menit)
          const cacheDate = new Date(cacheTimestamp);
          const now = new Date();
          const cacheFresh = (now.getTime() - cacheDate.getTime()) < 5 * 60 * 1000; // 5 menit
          
          if (cacheFresh) {
            try {
              const parsedData = JSON.parse(cachedData);
              if (Array.isArray(parsedData) && parsedData.length > 0) {
                console.log("Using fresh cache data:", parsedData.length, "items");
                setEquipment(parsedData);
                setLoading(false);
                setRecoveryMode(false);
                return;
              }
            } catch (e) {
              console.error("Error parsing cache:", e);
              // Continue to fetch from API
            }
          }
        }
      }

      // Buat URL dengan parameter nocache yang lebih efektif
      const timestamp = new Date().getTime();
      const url = `https://rumahakrilik.id/api/inventory/?category=equipment&nocache=${timestamp}`;
      
      // Tambahkan header yang lebih kuat untuk mencegah caching
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Pragma': 'no-cache',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'X-Requested-With': 'XMLHttpRequest' // Penting untuk bypass cache
      };

      console.log("Fetching equipment data...");
      const response = await axios.get(url, { headers });

      console.log('Raw API response:', response);
      
      let equipmentList = [];
      
      try {
        if (Array.isArray(response.data)) {
          equipmentList = response.data;
          console.log("Data dalam format array:", equipmentList.length);
        } else if (response.data.results && Array.isArray(response.data.results)) {
          equipmentList = response.data.results;
          console.log("Data dalam format paginated:", equipmentList.length);
        } else if (response.data.data && Array.isArray(response.data.data)) {
          equipmentList = response.data.data;
          console.log("Data dalam format data wrapper:", equipmentList.length);
        } else {
          console.error("Unexpected data format:", response.data);
          throw new Error("Format data tidak valid");
        }
        
        // Filter null/invalid data dan data non-equipment
        equipmentList = equipmentList.filter(item => 
          item && 
          typeof item === 'object' && 
          (item.category === 'equipment' || item.type === 'equipment')
        );
        
        if (equipmentList.length === 0) {
          console.warn("No equipment items found in response");
          // Jika tidak ada data equipment, coba jangan filter berdasarkan kategori
          if (Array.isArray(response.data)) {
            equipmentList = response.data;
          } else if (response.data.results && Array.isArray(response.data.results)) {
            equipmentList = response.data.results;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            equipmentList = response.data.data;
          }
        }
        
        const standardizedEquipment = equipmentList
          .filter(item => item !== null && typeof item === 'object') // Filter data yang valid
          .map(item => {
            try {
              console.log("Processing raw equipment:", item);
              
              let equipmentType = 'Lainnya'; // Default
              if (item.type && typeOptions.includes(item.type)) {
                equipmentType = item.type;
              } else if (item.subcategory && typeOptions.includes(item.subcategory)) {
                equipmentType = item.subcategory;
              } else if (item.asset_type && typeOptions.includes(item.asset_type)) {
                equipmentType = item.asset_type;
              }
              
              return {
                ...item,
                id: item.id || 0,
                name: item.name || 'Tidak ada nama',
                type: equipmentType,
                category: 'equipment',
                brand: item.brand || '',
                model: item.model || '',
                quantity: normalizeNumber(item.quantity) || normalizeNumber(item.current_stock) || normalizeNumber(item.stock) || 0,
                min_stock: normalizeNumber(item.min_stock) || normalizeNumber(item.minimum_stock) || 0,
                condition: item.condition || 'baik',
                price: normalizeNumber(item.price),
                location: item.location || '-',
                notes: item.notes || '-',
                sku: item.sku || `TOOL-${item.id || Date.now()}`
              };
            } catch (itemError) {
              console.error("Error processing equipment item:", itemError, item);
              return null;
            }
          })
          .filter(Boolean); // Filter null items
        
        const sortedEquipment = standardizedEquipment.sort((a, b) => {
          const conditionOrder = {
            'rusak': 0,
            'butuh_perawatan': 1,
            'cukup': 2,
            'baik': 3
          };
          
          const condA = conditionOrder[a.condition] !== undefined ? conditionOrder[a.condition] : 3;
          const condB = conditionOrder[b.condition] !== undefined ? conditionOrder[b.condition] : 3;
          
          return condB - condA;
        });
        
        console.log("Processed equipment data:", sortedEquipment.length, "items");
        
        // Penting: Pastikan array tidak kosong sebelum memperbarui state
        if (sortedEquipment.length > 0) {
          setEquipment(sortedEquipment);
          setRecoveryMode(false);
          
          try {
            localStorage.setItem('rumahAkrilik_equipmentBackup', JSON.stringify(sortedEquipment));
            localStorage.setItem('rumahAkrilik_equipmentBackupDate', new Date().toISOString());
            console.log("Auto backup to localStorage successful");
          } catch (backupError) {
            console.error("Error backing up to localStorage:", backupError);
          }
        } else {
          console.warn("No equipment data after processing, attempting recovery from local storage");
          attemptRecoveryFromLocalStorage();
        }
      } catch (processingError) {
        console.error("Error processing equipment data:", processingError);
        attemptRecoveryFromLocalStorage();
        throw processingError;
      }
    } catch (error) {
      console.error('Error fetching equipment:', error);
      
      // Coba dengan direct call ke API tanpa parameter
      try {
        console.log('Trying direct API call...');
        const token = localStorage.getItem('jwtToken');
        
        const fallbackResponse = await axios.get('https://rumahakrilik.id/api/inventory/', {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        if (fallbackResponse.status === 200) {
          try {
            console.log("Fallback API response:", fallbackResponse.data);
            let equipmentList = [];
            
            if (Array.isArray(fallbackResponse.data)) {
              equipmentList = fallbackResponse.data;
            } else if (fallbackResponse.data.results && Array.isArray(fallbackResponse.data.results)) {
              equipmentList = fallbackResponse.data.results;
            } else if (fallbackResponse.data.data && Array.isArray(fallbackResponse.data.data)) {
              equipmentList = fallbackResponse.data.data;
            }
            
            // Filter untuk equipment saja
            equipmentList = equipmentList.filter(item => 
              item && (
                item.category === 'equipment' || 
                item.asset_type === 'equipment' || 
                item.type === 'equipment'
              )
            );
            
            if (Array.isArray(equipmentList) && equipmentList.length > 0) {
              const standardizedEquipment = equipmentList
                .filter(item => item !== null)
                .map(item => ({
                  id: item.id || 0,
                  name: item.name || 'Tidak ada nama',
                  type: item.type || 'Lainnya',
                  category: 'equipment',
                  brand: item.brand || '',
                  model: item.model || '',
                  quantity: normalizeNumber(item.quantity) || normalizeNumber(item.current_stock) || 0,
                  min_stock: normalizeNumber(item.min_stock) || 0,
                  condition: item.condition || 'baik',
                  price: normalizeNumber(item.price),
                  location: item.location || '-',
                  notes: item.notes || '-',
                  sku: item.sku || `TOOL-${item.id || Date.now()}`
                }));
            
              console.log("Recovered using fallback method:", standardizedEquipment.length, "items");
              
              // Verifikasi data tidak kosong
              if (standardizedEquipment.length > 0) {
                setEquipment(standardizedEquipment);
                setRecoveryMode(false);
                
                // Cache data yang berhasil diambil
                localStorage.setItem('rumahAkrilik_equipmentBackup', JSON.stringify(standardizedEquipment));
                localStorage.setItem('rumahAkrilik_equipmentBackupDate', new Date().toISOString());
                
                toast.info('Data diambil dengan metode alternatif');
                return;
              }
            }
            console.warn("Fallback returned no equipment items");
            attemptRecoveryFromLocalStorage();
          } catch (processingError) {
            console.error("Error processing fallback data:", processingError);
            attemptRecoveryFromLocalStorage();
          }
        } else {
          attemptRecoveryFromLocalStorage();
        }
      } catch (fallbackError) {
        console.error('Fallback method failed:', fallbackError);
        attemptRecoveryFromLocalStorage();
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Perbaikan fungsi refreshData untuk menyamakannya dengan implementasi di DaftarAset
  const refreshData = async () => {
    try {
      setLoading(true);
      toast.info('Menyinkronkan data...');
      
      // Hapus cache local storage dahulu untuk memastikan data baru diambil
      localStorage.removeItem('rumahAkrilik_equipmentBackupDate');
      
      const timestamp = new Date().getTime();
      const token = localStorage.getItem('jwtToken');
      
      if (!token) {
        toast.error('Sesi login telah berakhir');
        return;
      }
      
      // Array untuk menampung semua data dari semua halaman
      let allEquipment = [];
      let currentPage = 1;
      let hasMorePages = true;
      
      // Fungsi untuk mengambil satu halaman data
      const fetchPage = async (page) => {
        const url = `https://rumahakrilik.id/api/inventory/?category=equipment&page=${page}&t=${timestamp}`;
        
        console.log(`Fetching page ${page}...`);
        const response = await axios.get(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate', 
            'Pragma': 'no-cache'
          }
        });
        
        return response.data;
      };
      
      // Loop untuk mengambil semua halaman data
      while (hasMorePages) {
        try {
          const pageData = await fetchPage(currentPage);
          
          // Periksa apakah data dalam format yang diharapkan
          if (Array.isArray(pageData)) {
            // Format array langsung
            allEquipment = [...allEquipment, ...pageData];
            hasMorePages = false; // Tidak ada data paginasi, jadi tidak ada halaman berikutnya
          } else if (pageData && Array.isArray(pageData.results)) {
            // Format paginasi
            allEquipment = [...allEquipment, ...pageData.results];
            hasMorePages = !!pageData.next;
          } else if (pageData && pageData.data && Array.isArray(pageData.data)) {
            // Format data wrapper
            allEquipment = [...allEquipment, ...pageData.data];
            hasMorePages = !!pageData.next_page;
          } else {
            console.warn("Unexpected data format on page", currentPage, pageData);
            hasMorePages = false;
          }
          
          currentPage++;
          
          // Safety limit untuk mencegah loop tak terbatas
          if (currentPage > 10) {
            console.warn("Reached maximum page limit (10), stopping pagination fetch");
            hasMorePages = false;
          }
        } catch (pageError) {
          console.error(`Error fetching page ${currentPage}:`, pageError);
          // Hentikan pagination jika terjadi error
          hasMorePages = false;
        }
      }
      
      console.log(`Total items fetched from all pages: ${allEquipment.length}`);
      
      // Filter hanya data equipment
      const equipmentData = allEquipment.filter(item => 
        item && (
          item.category === 'equipment' || 
          item.type === 'equipment' ||
          item.asset_type === 'equipment'
        )
      );
      
      // PENTING: Jika data kosong, ambil semua data tanpa filter kategori sebagai fallback
      if (equipmentData.length === 0 && allEquipment.length > 0) {
        console.warn("No equipment found with category filter, using all inventory items");
      }
      
      // Proses dan normalisasi data
      if (equipmentData.length > 0) {
        const processedData = equipmentData
          .filter(item => item !== null && typeof item === 'object')
          .map(item => {
            try {
              let equipmentType = 'Lainnya'; // Default
              if (item.type && typeOptions.includes(item.type)) {
                equipmentType = item.type;
              } else if (item.subcategory && typeOptions.includes(item.subcategory)) {
                equipmentType = item.subcategory;
              } else if (item.asset_type && typeOptions.includes(item.asset_type)) {
                equipmentType = item.asset_type;
              }
              
              return {
                ...item,
                id: item.id || 0,
                name: item.name || 'Tidak ada nama',
                type: equipmentType,
                category: 'equipment',
                brand: item.brand || '',
                model: item.model || '',
                quantity: normalizeNumber(item.quantity || item.current_stock || item.stock),
                min_stock: normalizeNumber(item.min_stock || item.minimum_stock),
                condition: item.condition || 'baik',
                price: normalizeNumber(item.price),
                location: item.location || '-',
                notes: item.notes || '-',
                sku: item.sku || `TOOL-${item.id}`
              };
            } catch (itemError) {
              console.error("Error processing item:", itemError);
              return null;
            }
          })
          .filter(Boolean); // Filter null items
        
        console.log("After processing:", processedData.length, "valid items");
        
        // Jangan update state jika data hasil proses kosong
        if (processedData.length === 0) {
          console.error("No valid equipment items after processing");
          toast.warning('Tidak ada data peralatan yang valid ditemukan');
          attemptRecoveryFromLocalStorage();
          return;
        }
        
        // Update state
        setEquipment(processedData);
        
        // Update cache lokal
        localStorage.setItem('rumahAkrilik_equipmentBackup', JSON.stringify(processedData));
        localStorage.setItem('rumahAkrilik_equipmentBackupDate', new Date().toISOString());
        
        setRecoveryMode(false);
        toast.success('Data peralatan berhasil disinkronkan');
        console.log(`Loaded ${processedData.length} items successfully`);
      } else {
        console.warn("No equipment data found in API response");
        if (equipment.length === 0) {
          // Jika tidak ada data peralatan sebelumnya
          toast.warning('Tidak ada data peralatan yang ditemukan');
          attemptRecoveryFromLocalStorage();
        } else {
          // Jika sudah ada data sebelumnya, tetap gunakan data tersebut
          toast.info('Tidak ada data baru, menggunakan data yang ada');
        }
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast.error('Gagal menyegarkan data: ' + error.message);
      
      // Coba gunakan data lokal jika refresh gagal
      attemptRecoveryFromLocalStorage();
    } finally {
      setLoading(false);
    }
  };

  // Fungsi handleRefreshClick yang benar
  const handleRefreshClick = async () => {
    if (loading) return;
    
    try {
      setLoading(true);
      toast.info('Memuat ulang data...');
      
      // Simpan state peralatan saat ini sebagai cadangan
      const currentEquipment = [...equipment];
      
      try {
        // PENTING: Gunakan refreshData() bukan fetchEquipment()
        // untuk memastikan semua data diambil dengan pagination
        await refreshData();
        toast.success('Data berhasil dimuat ulang');
      } catch (error) {
        console.error('Error refreshing equipment data:', error);
        
        // Kembalikan state ke data sebelumnya jika gagal dan tidak ada recovery
        if (equipment.length === 0 && currentEquipment.length > 0) {
          setEquipment(currentEquipment);
          toast.info('Menggunakan data sebelumnya');
        } else if (equipment.length === 0) {
          toast.error('Gagal memuat data peralatan');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Modifikasi handleCreate untuk memastikan data muncul di UI
  const handleCreate = async () => {
    try {
      if (!validateForm()) return;
      
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        toast.error('Sesi login telah berakhir');
        return;
      }
      
      let uniqueSku = formData.sku;
      if (!uniqueSku) {
        const cleanName = formData.name.replace(/[^a-zA-Z0-9]/g, '').substring(0, 5).toUpperCase();
        uniqueSku = `TOOL-${cleanName}-${Date.now().toString().slice(-4)}`;
      }
      
      const equipmentData = {
        name: formData.name.trim(),
        category: 'equipment',
        type: formData.type || 'Lainnya',
        brand: formData.brand || '',
        model: formData.model || '',
        quantity: String(normalizeNumber(formData.quantity) || 0),
        min_stock: String(normalizeNumber(formData.min_stock) || 0),
        condition: formData.condition || 'baik',
        price: String(normalizeNumber(formData.price) || 0),
        location: formData.location || '',
        notes: formData.notes || '',
        sku: uniqueSku
      };
      
      console.log("Data yang akan dikirim (format sederhana):", equipmentData);
      
      // Catat data di local state terlebih dahulu untuk optimistic UI update
      const newItem = {
        id: `temp-${Date.now()}`,
        ...equipmentData,
        quantity: normalizeNumber(formData.quantity),
        min_stock: normalizeNumber(formData.min_stock),
        price: normalizeNumber(formData.price)
      };
      
      try {
        setLoading(true); 

        // TAMBAHAN: Optimistic UI Update (sebelum request ke server)
        setEquipment(prevEquipment => [...prevEquipment, newItem]);
        
        const response = await axios.post(
          'https://rumahakrilik.id/api/inventory/',
          equipmentData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache'
            }
          }
        );
        
        console.log("Response dari server:", response);
        
        if (response.status === 201 || response.status === 200) {
          toast.success('Peralatan berhasil ditambahkan');
          
          // Perbarui state dengan data dari server
          const serverItem = response.data;
          
          // Update item ID dengan ID dari server
          setEquipment(prevEquipment => {
            return prevEquipment.map(item => {
              if (item.id === newItem.id) {
                return {
                  ...item,
                  id: serverItem.id,
                  sku: serverItem.sku || item.sku
                };
              }
              return item;
            });
          });
          
          // Reset form & close modal
          setFormData({
            name: '',
            sku: '',
            type: '',
            brand: '',
            model: '',
            quantity: '0',
            min_stock: '0',
            condition: 'baik',
            price: '0',
            location: '',
            notes: '',
            category: 'equipment'
          });
          setShowModal(false);
          
          // Update cache dengan data terbaru
          setTimeout(() => {
            try {
              const currentEquipment = [...equipment];
              const itemIndex = currentEquipment.findIndex(item => item.id === newItem.id);
              
              if (itemIndex >= 0) {
                currentEquipment[itemIndex] = {
                  ...currentEquipment[itemIndex],
                  id: serverItem.id,
                  sku: serverItem.sku || currentEquipment[itemIndex].sku
                };
                
                localStorage.setItem('rumahAkrilik_equipmentBackup', JSON.stringify(currentEquipment));
                localStorage.setItem('rumahAkrilik_equipmentBackupDate', new Date().toISOString());
              }
            } catch (error) {
              console.error("Error updating local cache:", error);
            }
          }, 0);
        }
      } catch (requestError) {
        console.error("Request error:", requestError);
        toast.warning('Terjadi kesalahan pada server');
        
        // Hapus item temporary dari UI jika terjadi error
        setEquipment(prevEquipment => 
          prevEquipment.filter(item => item.id !== newItem.id)
        );
        
        if (requestError.response) {
          console.log("Response error status:", requestError.response.status);
          console.log("Response error data:", requestError.response.data);
        }
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error in handleCreate:', error);
      toast.error(`Terjadi kesalahan: ${error.message}`);
      setLoading(false);
    }
  };

  const validateForm = () => {
    if (!formData.name || formData.name.trim() === '') {
      toast.error('Nama peralatan harus diisi');
      return false;
    }
    
    if (!formData.sku || formData.sku.trim() === '') {
      if (!editItem || !editItem.sku) {
      }
    }
    
    const quantity = normalizeNumber(formData.quantity);
    const minStock = normalizeNumber(formData.min_stock);
    const price = normalizeNumber(formData.price);
    
    if (isNaN(quantity) || quantity < 0) {
      toast.error('Jumlah stok harus berupa angka lebih dari atau sama dengan 0');
      return false;
    }
    
    if (isNaN(minStock) || minStock < 0) {
      toast.error('Minimum stok harus berupa angka lebih dari atau sama dengan 0');
      return false;
    }
    
    if (isNaN(price) || price < 0) {
      toast.error('Harga harus berupa angka lebih dari atau sama dengan 0');
      return false;
    }
    
    return true;
  };

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus peralatan ini?')) {
      try {
        setLoading(true);
        const token = localStorage.getItem('jwtToken');
        
        if (!token) {
          toast.error('Sesi login telah berakhir');
          return;
        }
        
        const response = await axios.delete(`https://rumahakrilik.id/api/inventory/${id}/`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache'
          }
        });
        
        if (response.status === 204 || response.status === 200) {
          toast.success('Peralatan berhasil dihapus');
          fetchEquipment(true);
        } else {
          throw new Error(`Error: ${response.status}`);
        }
      } catch (error) {
        console.error('Error deleting equipment:', error);
        toast.error('Gagal menghapus peralatan');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      name: item.name || '',
      sku: item.sku || '',
      type: item.type || '',
      brand: item.brand || '',
      model: item.model || '',
      quantity: String(item.quantity || 0),
      min_stock: String(item.min_stock || 0),
      condition: item.condition || 'baik',
      price: String(item.price || 0),
      location: item.location || '',
      notes: item.notes || '',
      category: 'equipment'
    });
    setShowModal(true);
  };

  // Perbaikan handleUpdate yang komprehensif
  const handleUpdate = async () => {
    try {
      if (!validateForm()) return;
      
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        toast.error('Sesi login telah berakhir');
        return;
      }
      
      setLoading(true);
      
      // Pastikan semua nilai numerik dikirim sebagai string untuk API
      const updatePayload = {
        name: formData.name.trim(),
        category: 'equipment',
        type: formData.type || 'Lainnya',
        brand: formData.brand || '',
        model: formData.model || '',
        quantity: String(normalizeNumber(formData.quantity)),
        min_stock: String(normalizeNumber(formData.min_stock)),
        condition: formData.condition,
        price: String(normalizeNumber(formData.price)),
        location: formData.location || '',
        notes: formData.notes || '',
        sku: formData.sku || editItem.sku || ''
      };
      
      // Perbarui state secara lokal terlebih dahulu (optimistic update)
      const optimisticUpdatedItem = {
        id: editItem.id,
        ...updatePayload,
        quantity: normalizeNumber(formData.quantity),
        min_stock: normalizeNumber(formData.min_stock),
        price: normalizeNumber(formData.price)
      };
      
      // Update lokal state terlebih dahulu
      setEquipment(prevEquipment => {
        return prevEquipment.map(item => 
          item.id === editItem.id ? optimisticUpdatedItem : item
        );
      });
      
      console.log("Sending update to API:", updatePayload);
      console.log("Original item ID:", editItem.id);
      
      try {
        // Gunakan fetch API yang lebih reliable untuk update
        const response = await fetch(`https://rumahakrilik.id/api/inventory/${editItem.id}/`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          },
          body: JSON.stringify(updatePayload)
        });
        
        // Periksa status response
        if (!response.ok) {
          const errorText = await response.text();
          console.error("Server error response:", errorText);
          throw new Error(`Error update: ${response.status} ${response.statusText}`);
        }
        
        // Coba parse response
        let responseData;
        try {
          responseData = await response.json();
          console.log("Update response:", responseData);
        } catch (parseError) {
          console.warn("Could not parse response as JSON:", parseError);
          // Tetap lanjutkan karena update mungkin berhasil meskipun format respons tidak valid
        }
        
        toast.success('Peralatan berhasil diperbarui');
        setShowModal(false);
        
        // Sinkronkan data dengan server
        setTimeout(() => {
          refreshData().catch(err => {
            console.error("Error refreshing data after update:", err);
            // Fallback ke cache lokal jika refresh gagal
            toast.info("Menggunakan data lokal karena gagal sinkronisasi dengan server");
          });
        }, 1000);
        
      } catch (updateError) {
        console.error("Update error:", updateError);
        toast.error(`Gagal update: ${updateError.message}`);
        
        // Pastikan UI tetap konsisten dengan mengembalikan state original
        fetchEquipment(true).catch(err => {
          console.error("Error fetching data after update failure:", err);
        });
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error in update process:", error);
      toast.error('Terjadi kesalahan saat memproses update');
      setLoading(false);
    }
  };
  
  // Helper function untuk menangani error update
  const handleUpdateError = (requestError) => {
    // Coba cek jika update mungkin berhasil meskipun ada error
    if (requestError.response && 
        ((requestError.response.status >= 200 && requestError.response.status < 300) ||
          (requestError.response.data && requestError.response.data.id))) {
      
      // Jika ada indikasi bahwa update sebenarnya berhasil
      toast.info('Data mungkin berhasil diperbarui, menyegarkan data...');
      
      // Update local state dulu
      setEquipment(prevEquipment => prevEquipment.map(item => 
        item.id === editItem.id 
          ? {
              ...item,
              name: formData.name,
              type: formData.type || 'Lainnya',
              brand: formData.brand || '',
              model: formData.model || '',
              quantity: normalizeNumber(formData.quantity),
              min_stock: normalizeNumber(formData.min_stock),
              condition: formData.condition,
              price: normalizeNumber(formData.price),
              location: formData.location || '',
              notes: formData.notes || '',
            }
          : item
      ));
      
      setShowModal(false);
      
      // Coba refresh untuk memastikan
      setTimeout(() => fetchEquipment(true), 2000);
      return;
    }
    
    // Handle error sesuai dengan response status
    if (requestError.response) {
      if (requestError.response.status === 500) {
        toast.error('Gagal memperbarui peralatan: Terjadi kesalahan pada server');
      } else if (requestError.response.data) {
        if (typeof requestError.response.data === 'object') {
          const errorMessages = Object.entries(requestError.response.data)
            .map(([key, value]) => {
              if (Array.isArray(value)) return `${key}: ${value.join(', ')}`;
              return `${key}: ${value}`;
            })
            .join('; ');
          toast.error(`Gagal memperbarui peralatan: ${errorMessages}`);
        } else {
          toast.error(`Gagal memperbarui peralatan: ${requestError.response.data}`);
        }
      } else {
        toast.error(`Gagal memperbarui peralatan (${requestError.response.status})`);
      }
    } else {
      toast.error(`Gagal terhubung ke server: ${requestError.message}`);
    }
  };

  const resetForm = () => {
    setEditItem(null);
    setFormData({
      name: '',
      sku: '',
      type: '',
      brand: '',
      model: '',
      quantity: '0',
      min_stock: '0',
      condition: 'baik',
      price: '0',
      location: '',
      notes: '',
      category: 'equipment'
    });
  };

  const getConditionBadge = (condition) => {
    const option = conditionOptions.find(opt => opt.value === condition) || conditionOptions[0];
    return <Badge bg={option.variant}>{option.label}</Badge>;
  };

  const getFilteredEquipment = () => {
    // Pastikan equipment ada dan berisi data
    if (!equipment || !Array.isArray(equipment)) {
      console.warn("Equipment data is not an array:", equipment);
      return [];
    }
    
    console.log(`Filtering ${equipment.length} equipment items`);
    
    let filteredBySearch = equipment;
    
    if (searchTerm && searchTerm.trim() !== '') {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filteredBySearch = equipment.filter(item => {
        // Pastikan properti ada sebelum mencoba toLowerCase
        return (
          (item.name && item.name.toString().toLowerCase().includes(lowerSearchTerm)) ||
          (item.sku && item.sku.toString().toLowerCase().includes(lowerSearchTerm)) ||
          (item.type && item.type.toString().toLowerCase().includes(lowerSearchTerm)) ||
          (item.brand && item.brand.toString().toLowerCase().includes(lowerSearchTerm)) ||
          (item.model && item.model && item.model.toString().toLowerCase().includes(lowerSearchTerm)) ||
          (item.location && item.location.toString().toLowerCase().includes(lowerSearchTerm))
        );
      });
    }
    
    if (statusFilter !== 'all') {
      filteredBySearch = filteredBySearch.filter(item => item.condition === statusFilter);
    }
    
    // Debug info
    console.log(`Found ${filteredBySearch.length} items after filtering`);
    
    return filteredBySearch;
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();
    
    if (editItem) {
      handleUpdate();
    } else {
      handleCreate();
    }
  };

  return (
    <MainLayout>
      <Container fluid className="py-4">
        <h1 className="mb-4">Daftar Peralatan</h1>
        
        {recoveryMode && (
          <div className="alert alert-warning mb-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            <strong>Mode Pemulihan:</strong> Menggunakan data cache lokal. Klik "Sinkronisasi" untuk mencoba terhubung ke server lagi.
          </div>
        )}
        
        <div className="d-flex justify-content-between mb-3">
          <InputGroup style={{ maxWidth: '400px' }}>
            <InputGroup.Text>
              <FontAwesomeIcon icon={faSearch} />
            </InputGroup.Text>
            <Form.Control
              placeholder="Cari berdasarkan nama, tipe, merek, lokasi..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          
          <div>
            <Button 
              variant="warning" 
              className="me-2" 
              onClick={refreshData}
              disabled={loading}
            >
              {loading ? (
                <><Spinner animation="border" size="sm" className="me-1" /> Sinkronisasi...</>
              ) : (
                <><FontAwesomeIcon icon={faSync} className="me-1" /> Sinkronisasi</>
              )}
            </Button>
            <Button 
              variant="outline-secondary" 
              className="me-2" 
              onClick={handleRefreshClick}
              disabled={loading}
            >
              {loading ? (
                <><Spinner animation="border" size="sm" className="me-1" /> Memuat...</>
              ) : (
                <><FontAwesomeIcon icon={faSync} className="me-1" /> Refresh Data</>
              )}
            </Button>
            <Button 
              variant="primary" 
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              disabled={loading}
            >
              <FontAwesomeIcon icon={faPlus} className="me-2" /> Tambah Peralatan
            </Button>
          </div>
        </div>

        <div className="mb-3">
          <Button 
            variant="light"
            size="sm" 
            onClick={() => setDebugMode(!debugMode)}
            className="me-2"
          >
            {debugMode ? 'Sembunyikan Debug' : 'Tampilkan Debug'}
          </Button>
          
          {debugMode && (
            <div className="p-2 bg-light border rounded mt-1">
              <p><small>Jumlah Data: {equipment?.length || 0}</small></p>
              <small className="text-muted">Mode Debug: {recoveryMode ? 'Cache Recovery' : 'Normal'}</small>
            </div>
          )}
        </div>

        {debugMode && (
          <div className="mt-3 p-2 bg-light border">
            <small>Equipment data length: {equipment?.length || 0}</small>
            <pre style={{fontSize: '0.7rem', maxHeight: '100px', overflow: 'auto'}}>
              {JSON.stringify(equipment?.slice(0, 3), null, 2)}
            </pre>
          </div>
        )}

        {debugMode && (
          <div className="d-flex gap-2 mb-2">
            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={() => {
                console.log("Current equipment state:", equipment);
                toast.info(`Data state: ${equipment.length} item`);
              }}
            >
              Log Equipment State
            </Button>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={async () => {
                try {
                  const token = localStorage.getItem('jwtToken');
                  const response = await fetch('https://rumahakrilik.id/api/inventory/?category=equipment', {
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Cache-Control': 'no-cache'
                    }
                  });
                  const text = await response.text();
                  console.log("Raw API response:", text);
                  toast.info('API response logged to console');
                } catch (e) {
                  console.error("API test error:", e);
                  toast.error('API test failed: ' + e.message);
                }
              }}
            >
              Test API
            </Button>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={() => {
                // Diagnose API Response
                setLoading(true);
                const token = localStorage.getItem('jwtToken');
                Promise.all([
                  fetch('https://rumahakrilik.id/api/inventory/?category=equipment', {
                    headers: { 'Authorization': `Bearer ${token}`, 'Cache-Control': 'no-cache' }
                  }).then(r => r.text()),
                  fetch('https://rumahakrilik.id/api/inventory/', {
                    headers: { 'Authorization': `Bearer ${token}`, 'Cache-Control': 'no-cache' }
                  }).then(r => r.text())
                ]).then(([equipmentResponse, allResponse]) => {
                  console.log("Equipment-only endpoint:", equipmentResponse);
                  console.log("All inventory endpoint:", allResponse);
                  
                  try {
                    const equipmentData = JSON.parse(equipmentResponse);
                    const allData = JSON.parse(allResponse);
                    
                    console.log(`Equipment endpoint returned ${Array.isArray(equipmentData) ? equipmentData.length : 'non-array'} items`);
                    console.log(`All inventory endpoint returned ${Array.isArray(allData) ? allData.length : 'non-array'} items`);
                    
                    const equipmentCount = Array.isArray(equipmentData) ? 
                      equipmentData.filter(i => i && i.category === 'equipment').length : 0;
                      
                    const allEquipmentCount = Array.isArray(allData) ?
                      allData.filter(i => i && i.category === 'equipment').length : 0;
                      
                    toast.info(`API diagnosis: ${equipmentCount} vs ${allEquipmentCount} equipment items found`);
                  } catch (e) {
                    console.error("Error parsing API responses:", e);
                    toast.error("API returned invalid JSON");
                  }
                  
                  setLoading(false);
                }).catch(err => {
                  console.error("API diagnosis error:", err);
                  toast.error("API diagnosis failed");
                  setLoading(false);
                });
              }}
            >
              Diagnose API
            </Button>
          </div>
        )}

        <Card>
          <Card.Body>
            <div className="mb-3">
              <button 
                className={`btn btn-sm mx-1 ${statusFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setStatusFilter('all')}
              >
                Semua
              </button>
              <button 
                className={`btn btn-sm mx-1 ${statusFilter === 'baik' ? 'btn-success' : 'btn-outline-success'}`}
                onClick={() => setStatusFilter('baik')}
              >
                Baik
              </button>
              <button 
                className={`btn btn-sm mx-1 ${statusFilter === 'cukup' ? 'btn-info' : 'btn-outline-info'}`}
                onClick={() => setStatusFilter('cukup')}
              >
                Cukup
              </button>
              <button 
                className={`btn btn-sm mx-1 ${statusFilter === 'butuh_perawatan' ? 'btn-warning' : 'btn-outline-warning'}`}
                onClick={() => setStatusFilter('butuh_perawatan')}
              >
                Butuh Perawatan
              </button>
              <button 
                className={`btn btn-sm mx-1 ${statusFilter === 'rusak' ? 'btn-danger' : 'btn-outline-danger'}`}
                onClick={() => setStatusFilter('rusak')}
              >
                Rusak
              </button>
            </div>
            
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Memuat data peralatan...</p>
              </div>
            ) : (
              <>
                {getFilteredEquipment().length === 0 ? (
                  <div className="text-center py-5">
                    <FontAwesomeIcon icon={faTools} size="3x" className="text-muted mb-3" />
                    <p>Tidak ada data peralatan</p>
                    <Button variant="outline-primary" onClick={() => {
                      resetForm();
                      setShowModal(true);
                    }}>
                      Tambah Peralatan Baru
                    </Button>
                  </div>
                ) : (
                  <Table responsive striped bordered hover>
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Nama Peralatan</th>
                        <th>Tipe</th>
                        <th>Merek & Model</th>
                        <th>Stock</th>
                        <th>Min. Stock</th>
                        <th>Kondisi</th>
                        <th>Harga</th>
                        <th>Lokasi</th>
                        <th>Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredEquipment().map((item) => (
                        <tr key={item.id} className={parseInt(item.quantity || 0) <= parseInt(item.min_stock || 0) ? 'table-warning' : ''}>
                          <td>{item.sku}</td>
                          <td>{item.name}</td>
                          <td>{item.type || 'Lainnya'}</td>
                          <td>{item.brand} {item.model && `(${item.model})`}</td>
                          <td className={parseInt(item.quantity || 0) <= parseInt(item.min_stock || 0) ? 'fw-bold text-danger' : ''}>
                            {parseInt(item.quantity || 0)}
                          </td>
                          <td>{parseInt(item.min_stock || 0)}</td>
                          <td>{getConditionBadge(item.condition)}</td>
                          <td>{formatRupiah(item.price || 0)}</td>
                          <td>{item.location}</td>
                          <td>
                            <Button variant="outline-primary" size="sm" className="me-1" onClick={() => handleEdit(item)}>
                              <FontAwesomeIcon icon={faEdit} />
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(item.id)}>
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </>
            )}
          </Card.Body>
        </Card>

        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editItem ? `Edit Peralatan: ${editItem.name}` : 'Tambah Peralatan Baru'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmitForm}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nama Peralatan</Form.Label>
                    <Form.Control 
                      type="text" 
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>SKU/Kode</Form.Label>
                    <Form.Control 
                      type="text"
                      placeholder="Kode akan dibuat otomatis jika kosong"
                      value={formData.sku}
                      onChange={(e) => setFormData({...formData, sku: e.target.value})}
                    />
                    <Form.Text className="text-muted">
                      Kode unik untuk peralatan ini. Akan dibuat otomatis jika tidak diisi.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Kategori</Form.Label>
                    <Form.Control 
                      type="text" 
                      value="Peralatan" 
                      disabled 
                      className="bg-light"
                    />
                    <Form.Text className="text-muted">
                      Kategori tidak dapat diubah
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Tipe</Form.Label>
                    <Form.Select
                      value={formData.type}
                      onChange={(e) => setFormData({...formData, type: e.target.value})}
                    >
                      <option value="Lainnya">-- Pilih Tipe --</option>
                      {typeOptions.map((type, index) => (
                        <option key={index} value={type}>{type}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Lokasi</Form.Label>
                    <Form.Control 
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Merek</Form.Label>
                    <Form.Control 
                      type="text"
                      value={formData.brand}
                      onChange={(e) => setFormData({...formData, brand: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Model</Form.Label>
                    <Form.Control 
                      type="text"
                      value={formData.model}
                      onChange={(e) => setFormData({...formData, model: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Jumlah Stok</Form.Label>
                    <Form.Control 
                      type="number"
                      min="0"
                      required
                      value={formData.quantity}
                      onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Minimum Stok</Form.Label>
                    <Form.Control 
                      type="number"
                      min="0"
                      value={formData.min_stock}
                      onChange={(e) => setFormData({...formData, min_stock: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Kondisi</Form.Label>
                    <Form.Select
                      value={formData.condition}
                      onChange={(e) => setFormData({...formData, condition: e.target.value})}
                    >
                      {conditionOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Harga</Form.Label>
                <InputGroup>
                  <InputGroup.Text>Rp</InputGroup.Text>
                  <Form.Control 
                    type="text"
                    value={formData.price}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^\d]/g, '');
                      setFormData({...formData, price: value});
                    }}
                  />
                </InputGroup>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Catatan</Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />
              </Form.Group>
              
              <div className="d-flex justify-content-end">
                <Button variant="secondary" className="me-2" onClick={() => setShowModal(false)}>
                  Batal
                </Button>
                <Button variant="primary" type="submit">
                  {editItem ? 'Perbarui' : 'Simpan'}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </Container>
    </MainLayout>
  );
};

export default DaftarPeralatan;